import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import AuthSplitLayout from '../../components/auth/AuthSplitLayout';
import { authService } from '../../services/authService';

const loginInputClass =
  'w-full border border-[#E5E7EB] rounded-xl bg-white text-[15px] text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:border-[#3B82F6] focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)]';

export default function ForgotPasswordPage() {
  usePageMeta(
    'Forgot password - SmileConsent',
    'Reset your SmileConsent password. Enter your email and we will send you a link to create a new password.'
  );

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      await authService.resetPassword(email);
      setMessage('Password reset email sent! Please check your inbox.');
    } catch (err) {
      console.error('Error sending password reset email:', err);
      setError(err.message || 'Unable to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const hasAlert = Boolean(message || error);

  return (
    <AuthSplitLayout centerContent>
      <div className="w-full max-w-[440px] mx-auto">
        <h1 className="text-[36px] font-bold text-[#111827] leading-tight tracking-tight">
          Forgot password?
        </h1>
        <p className="mt-2 text-[15px] text-[#6B7280]">
          Enter your email and we&apos;ll send you a link to reset your password.
        </p>

        {message && (
          <div className="mt-6 p-3.5 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm leading-relaxed">
            {message}
          </div>
        )}

        {error && (
          <div className="mt-6 p-3.5 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className={`space-y-4 ${hasAlert ? 'mt-6' : 'mt-8'}`}>
          <div className="relative">
            <EnvelopeIcon
              className="absolute left-4 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-gray-400 pointer-events-none"
              strokeWidth={1.75}
            />
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
                setMessage('');
              }}
              className={`${loginInputClass} py-[14px] pl-11 pr-4`}
              placeholder="Email address"
              aria-label="Email address"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full h-[52px] rounded-xl text-white text-base font-semibold border-0 transition-all hover:opacity-90 hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{ background: 'linear-gradient(135deg, #3B82F6, #6366F1)' }}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Sending…
              </span>
            ) : (
              'Send reset email'
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-[#6B7280]">
          Remember your password?{' '}
          <Link to="/login" className="font-semibold text-[#3B82F6] hover:text-blue-700">
            Back to sign in
          </Link>
        </p>
      </div>
    </AuthSplitLayout>
  );
}
